import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Filter, Paperclip, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY', 'AED', 'SGD'];

const formatMoney = (amount, currency) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount || 0);

const Transactions = () => {
  const { user, token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [pendingReceipt, setPendingReceipt] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: 'income',
    amount: '',
    currency: 'USD',
    category: '',
    project: '',
    department: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [filterType, setFilterType] = useState('all');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTransactions();
  }, [filterType]);

  const fetchTransactions = async () => {
    try {
      const params = filterType !== 'all' ? { type: filterType, limit: 1000 } : { limit: 1000 };
      const response = await axios.get(`${API}/transactions`, { params });
      setTransactions(response.data);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString(),
      };

      let txnId;
      if (editingTransaction) {
        const updatePayload = { ...payload };
        // Backend doesn't allow updating type
        delete updatePayload.type;
        await axios.put(`${API}/transactions/${editingTransaction.id}`, updatePayload);
        txnId = editingTransaction.id;
        toast.success('Transaction updated successfully');
      } else {
        const res = await axios.post(`${API}/transactions`, payload);
        txnId = res.data.id;
        toast.success('Transaction created successfully');
      }

      // Upload receipt if one is pending
      if (pendingReceipt && txnId) {
        const fd = new FormData();
        fd.append('file', pendingReceipt);
        try {
          await axios.post(`${API}/transactions/${txnId}/receipt`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          toast.success('Receipt uploaded');
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to upload receipt');
        }
      }

      setDialogOpen(false);
      resetForm();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save transaction');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;

    try {
      await axios.delete(`${API}/transactions/${id}`);
      toast.success('Transaction deleted successfully');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  const handleRemoveReceipt = async (transactionId) => {
    if (!window.confirm('Remove the receipt attached to this transaction?')) return;
    try {
      await axios.delete(`${API}/transactions/${transactionId}/receipt`);
      toast.success('Receipt removed');
      fetchTransactions();
    } catch {
      toast.error('Failed to remove receipt');
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setPendingReceipt(null);
    setFormData({
      type: transaction.type,
      amount: transaction.amount.toString(),
      currency: transaction.currency || 'USD',
      category: transaction.category,
      project: transaction.project || '',
      department: transaction.department || '',
      description: transaction.description,
      date: new Date(transaction.date).toISOString().split('T')[0],
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTransaction(null);
    setPendingReceipt(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setFormData({
      type: 'income',
      amount: '',
      currency: 'USD',
      category: '',
      project: '',
      department: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    });
  };

  const canEdit = user?.role !== 'viewer';

  const openReceipt = async (fileId) => {
    try {
      const res = await axios.post(`${API}/files/${fileId}/url`, null, { params: { ttl: 900 } });
      // res.data.url is a relative path like /api/files/{id}?token=...
      const fullUrl = res.data.url.startsWith('http') ? res.data.url : `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Could not open receipt');
    }
  };

  const handleOcrExtract = async () => {
    if (!pendingReceipt) {
      toast.error('Choose a receipt image or PDF first');
      return;
    }
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', pendingReceipt);
      const res = await axios.post(`${API}/receipts/extract`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const parsed = res.data?.parsed;
      if (!parsed) {
        toast.error('Could not parse receipt');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        type: 'expense',
        amount: parsed.amount != null ? String(parsed.amount) : prev.amount,
        currency: parsed.currency || prev.currency,
        category: parsed.category || prev.category,
        description:
          parsed.description ||
          (parsed.vendor ? `${parsed.vendor}` : prev.description),
        date: parsed.date || prev.date,
      }));
      toast.success('Receipt details extracted — review and save');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Receipt extraction failed');
    } finally {
      setOcrLoading(false);
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="transactions-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="transactions-title">
              Transactions
            </h1>
            <p className="text-slate-600 mt-1 font-ibm">Manage income and expenses</p>
          </div>
          <div className="flex gap-3">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger data-testid="filter-type" className="w-40 bg-slate-50 border-slate-200 rounded-sm">
                <Filter size={16} className="mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>
            {canEdit && (
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="add-transaction-button"
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
                  >
                    <Plus size={18} className="mr-2" />
                    Add Transaction
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-chivo text-2xl">
                      {editingTransaction ? 'Edit Transaction' : 'New Transaction'}
                    </DialogTitle>
                    <DialogDescription className="text-slate-600 font-ibm text-sm">
                      {editingTransaction ? 'Update transaction details below.' : 'Record a new income or expense entry.'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="transaction-form">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Type</Label>
                        <Select
                          value={formData.type}
                          onValueChange={(value) => setFormData({ ...formData, type: value })}
                          disabled={!!editingTransaction}
                        >
                          <SelectTrigger data-testid="input-type" className="mt-2 bg-slate-50 border-slate-200 rounded-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="income">Income</SelectItem>
                            <SelectItem value="expense">Expense</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Amount</Label>
                        <Input
                          data-testid="input-amount"
                          type="number"
                          step="0.01"
                          min="0.01"
                          required
                          value={formData.amount}
                          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                          className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Currency</Label>
                        <Select
                          value={formData.currency}
                          onValueChange={(value) => setFormData({ ...formData, currency: value })}
                        >
                          <SelectTrigger data-testid="input-currency" className="mt-2 bg-slate-50 border-slate-200 rounded-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CURRENCIES.map((c) => (
                              <SelectItem key={c} value={c}>
                                {c}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Category</Label>
                      <Input
                        data-testid="input-category"
                        required
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                        placeholder="e.g., Sales, Marketing, Rent"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Project</Label>
                        <Input
                          data-testid="input-project"
                          value={formData.project}
                          onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                          className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                          placeholder="Optional"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Department</Label>
                        <Input
                          data-testid="input-department"
                          value={formData.department}
                          onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                          className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Date</Label>
                      <Input
                        data-testid="input-date"
                        type="date"
                        required
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Description</Label>
                      <Textarea
                        data-testid="input-description"
                        required
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                        placeholder="Transaction details"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                        Receipt (Optional)
                      </Label>
                      <div className="mt-2 flex items-center gap-3">
                        <input
                          ref={fileInputRef}
                          data-testid="input-receipt"
                          type="file"
                          accept="image/jpeg,image/png,image/webp,application/pdf"
                          onChange={(e) => setPendingReceipt(e.target.files?.[0] || null)}
                          className="block w-full text-sm font-ibm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border file:border-slate-200 file:bg-slate-50 file:text-slate-900 file:font-ibm file:font-medium hover:file:bg-slate-100"
                        />
                        {pendingReceipt && (
                          <Button
                            type="button"
                            onClick={handleOcrExtract}
                            disabled={ocrLoading}
                            data-testid="ocr-extract-button"
                            className="flex-shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white rounded-sm whitespace-nowrap px-3 py-2"
                          >
                            <Sparkles size={14} className="mr-1" />
                            {ocrLoading ? 'Reading…' : 'Auto-fill'}
                          </Button>
                        )}
                        {editingTransaction?.receipt_id && !pendingReceipt && (
                          <span
                            data-testid="existing-receipt-indicator"
                            className="inline-flex items-center gap-1 text-xs font-ibm text-emerald-700 whitespace-nowrap"
                          >
                            <Paperclip size={14} /> Has receipt
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 font-ibm mt-1">
                        JPG, PNG, WEBP, or PDF · max 10 MB · Click <strong>Auto-fill</strong> to extract details via AI
                      </p>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button
                        type="submit"
                        data-testid="submit-transaction"
                        className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
                      >
                        {editingTransaction ? 'Update' : 'Create'}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => { setDialogOpen(false); resetForm(); }}
                        className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-sm hover:scale-[0.98] transition-transform"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-slate-900 font-ibm" data-testid="transactions-loading">Loading transactions...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="transactions-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Type</th>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Category</th>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Description</th>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Project</th>
                    <th className="px-6 py-4 text-right text-xs font-ibm font-semibold uppercase tracking-widest">Amount</th>
                    <th className="px-6 py-4 text-center text-xs font-ibm font-semibold uppercase tracking-widest">Receipt</th>
                    {canEdit && <th className="px-6 py-4 text-right text-xs font-ibm font-semibold uppercase tracking-widest">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan={canEdit ? 8 : 7} className="px-6 py-12 text-center text-slate-600 font-ibm" data-testid="no-transactions">
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    transactions.map((transaction, index) => (
                      <tr key={transaction.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`transaction-row-${index}`}>
                        <td className="px-6 py-4 font-ibm text-sm text-slate-900">
                          {new Date(transaction.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-block px-3 py-1 text-xs font-ibm font-semibold uppercase tracking-widest rounded-sm ${
                              transaction.type === 'income'
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-red-50 text-red-700'
                            }`}
                          >
                            {transaction.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-ibm text-sm text-slate-900">{transaction.category}</td>
                        <td className="px-6 py-4 font-ibm text-sm text-slate-600">{transaction.description}</td>
                        <td className="px-6 py-4 font-ibm text-sm text-slate-600">{transaction.project || '-'}</td>
                        <td
                          className={`px-6 py-4 text-right font-ibm font-semibold tabular-nums ${
                            transaction.type === 'income' ? 'text-emerald-600' : 'text-red-600'
                          }`}
                          data-testid={`transaction-amount-${index}`}
                        >
                          {transaction.type === 'income' ? '+' : '-'}
                          {formatMoney(transaction.amount, transaction.currency)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {(transaction.receipts && transaction.receipts.length > 0) || transaction.receipt_id ? (
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => openReceipt(transaction.receipts?.[transaction.receipts.length - 1] || transaction.receipt_id)}
                                data-testid={`receipt-link-${index}`}
                                className="text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1"
                                title={`View receipt${(transaction.receipts?.length || 1) > 1 ? `s (${transaction.receipts.length})` : ''}`}
                              >
                                <Paperclip size={14} />
                                {(transaction.receipts?.length || 0) > 1 && (
                                  <span className="text-xs font-ibm" data-testid={`receipt-count-${index}`}>
                                    {transaction.receipts.length}
                                  </span>
                                )}
                              </button>
                              {canEdit && (
                                <button
                                  onClick={() => handleRemoveReceipt(transaction.id)}
                                  data-testid={`receipt-remove-${index}`}
                                  className="text-slate-400 hover:text-red-600 transition-colors"
                                  title="Remove all receipts"
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-300 text-xs" data-testid={`no-receipt-${index}`}>—</span>
                          )}
                        </td>
                        {canEdit && (
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleEdit(transaction)}
                                data-testid={`edit-transaction-${index}`}
                                className="p-2 hover:bg-slate-100 rounded-sm transition-colors"
                                title="Edit"
                              >
                                <Edit size={16} className="text-slate-600" />
                              </button>
                              <button
                                onClick={() => handleDelete(transaction.id)}
                                data-testid={`delete-transaction-${index}`}
                                className="p-2 hover:bg-red-50 rounded-sm transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={16} className="text-red-600" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Transactions;